import { findSlotContainingTime, type TimeSlotRange } from "./timeBookingUtils";

export interface CountableReservation {
  time: string;
  persons: number;
  numberOfChildren?: number;
  status: string;
}

export interface CountBucket {
  reservations: number;
  people: number;
  children: number;
}

export interface ServiceCounts {
  day: CountBucket;
  services: Array<{ start: string; end: string } & CountBucket>;
  outOfService: CountBucket;
}

const empty = (): CountBucket => ({ reservations: 0, people: 0, children: 0 });

/** Réservations actives uniquement : annulées et liste d'attente exclues. */
export function isCountable(status: string): boolean {
  return status !== "annulé" && status !== "en_attente";
}

function add(b: CountBucket, r: CountableReservation) {
  const kids = r.numberOfChildren || 0;
  b.reservations += 1;
  b.people += (r.persons || 0) + kids;
  b.children += kids;
}

/**
 * Totaux du jour et par service. Une réservation va dans le premier service
 * contenant son heure de début (services après minuit compris), sinon « hors service ».
 * Invariant : somme(services) + outOfService = day.
 */
export function computeServiceCounts(
  reservations: CountableReservation[],
  services: TimeSlotRange[],
): ServiceCounts {
  const sorted = [...services].sort((a, b) => a.start.localeCompare(b.start));
  const result: ServiceCounts = {
    day: empty(),
    services: sorted.map((s) => ({ start: s.start, end: s.end, ...empty() })),
    outOfService: empty(),
  };
  for (const r of reservations) {
    if (!isCountable(r.status)) continue;
    add(result.day, r);
    const slot = findSlotContainingTime(r.time.substring(0, 5), sorted);
    if (slot) {
      const idx = sorted.indexOf(slot);
      add(result.services[idx], r);
    } else {
      add(result.outOfService, r);
    }
  }
  return result;
}
